import React, { useEffect, useState, useMemo } from 'react';
import { bookingService } from '../services/portalService';
import type { DoctorSummary, TimeSlot, BookingResponse } from '../../../types/portal';
import EmptyState from '../../../components/ui/EmptyState';

type Step = 'specialty' | 'doctor' | 'date' | 'slot' | 'confirm';

interface SpecialtyGroup {
    id: number;
    name: string;
}

const BookAppointmentPage: React.FC = () => {
    const [step, setStep] = useState<Step>('specialty');

    // Data
    const [doctors, setDoctors] = useState<DoctorSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Selections
    const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<number | null>(null);
    const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
    const [reason, setReason] = useState('');

    // Slot data
    const [slots, setSlots] = useState<TimeSlot[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // Booking state
    const [bookingResult, setBookingResult] = useState<BookingResponse | null>(null);
    const [bookingError, setBookingError] = useState<string | null>(null);
    const [bookingLoading, setBookingLoading] = useState(false);

    // Load doctors on mount
    useEffect(() => {
        bookingService
            .getDoctors()
            .then((data) => {
                const active = data.filter((d) => d.is_active);
                setDoctors(active);
            })
            .catch((err) => setError(err.response?.data?.message || 'No pudimos cargar la lista de médicos. Por favor, inténtalo de nuevo más tarde.'))
            .finally(() => setLoading(false));
    }, []);

    // Derive unique specialties from doctors
    const specialties: SpecialtyGroup[] = useMemo(() => {
        const map = new Map<number, string>();
        doctors.forEach((d) => {
            if (d.specialty && !map.has(d.specialty.id)) {
                map.set(d.specialty.id, d.specialty.name);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [doctors]);

    // Filter doctors by selected specialty
    const filteredDoctors = useMemo(() => {
        if (!selectedSpecialtyId) return [];
        return doctors.filter((d) => d.specialty?.id === selectedSpecialtyId);
    }, [doctors, selectedSpecialtyId]);

    // Get selected doctor name
    const selectedDoctor = useMemo(
        () => doctors.find((d) => d.id === selectedDoctorId) || null,
        [doctors, selectedDoctorId],
    );

    // Load slots when date + doctor are selected
    useEffect(() => {
        if (selectedDoctorId && selectedDate) {
            setLoadingSlots(true);
            bookingService
                .getSlots(selectedDoctorId, selectedDate)
                .then((data) => setSlots(data))
                .catch((err) => setError(err.response?.data?.message || 'No pudimos cargar los horarios disponibles. Por favor, inténtalo de nuevo.'))
                .finally(() => setLoadingSlots(false));
        } else {
            setSlots([]);
        }
    }, [selectedDoctorId, selectedDate]);

    // Today's date for min date input
    const today = new Date().toISOString().split('T')[0];

    const handleBook = async () => {
        if (!selectedDoctorId || !selectedSlot) return;

        setBookingLoading(true);
        setBookingError(null);

        try {
            const result = await bookingService.bookAppointment({
                doctor_id: selectedDoctorId,
                time_slot_id: selectedSlot.id,
                reason: reason || undefined,
            });
            setBookingResult(result);
            setStep('confirm');
        } catch (err: any) {
            setBookingError(err.response?.data?.message || 'No pudimos agendar tu cita. Por favor, inténtalo de nuevo.');
        } finally {
            setBookingLoading(false);
        }
    };

    const resetForm = () => {
        setStep('specialty');
        setSelectedSpecialtyId(null);
        setSelectedDoctorId(null);
        setSelectedDate('');
        setSelectedSlot(null);
        setReason('');
        setBookingResult(null);
        setBookingError(null);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="book-appointment-page">
            <h3 className="mb-4">
                <i className="fas fa-calendar-plus me-2"></i>
                Agendar Cita
            </h3>

            {/* Step indicator */}
            <div className="booking-steps d-flex mb-4">
                {(['specialty', 'doctor', 'date', 'slot', 'confirm'] as Step[]).map((s, i) => (
                    <div
                        key={s}
                        className={`step-indicator d-flex align-items-center ${
                            step === s ? 'text-primary fw-bold' : 'text-muted'
                        }`}
                    >
                        <span
                            className={`step-circle me-1 d-inline-flex align-items-center justify-content-center rounded-circle ${
                                step === s ? 'bg-primary text-white' : 'bg-light text-muted'
                            }`}
                            style={{ width: 28, height: 28, fontSize: 13 }}
                        >
                            {i + 1}
                        </span>
                        <span className="d-none d-md-inline small">
                            {s === 'specialty' && 'Especialidad'}
                            {s === 'doctor' && 'Médico'}
                            {s === 'date' && 'Fecha'}
                            {s === 'slot' && 'Horario'}
                            {s === 'confirm' && 'Confirmar'}
                        </span>
                        {i < 4 && <span className="mx-1 text-muted">→</span>}
                    </div>
                ))}
            </div>

            {/* Step: Specialty Selection */}
            {step === 'specialty' && (
                <div className="card">
                    <div className="card-body">
                        <h5>Selecciona una especialidad</h5>
                        <div className="row g-3 mt-2">
                            {specialties.map((sp) => (
                                <div className="col-md-4" key={sp.id}>
                                    <button
                                        className={`btn w-100 ${
                                            selectedSpecialtyId === sp.id
                                                ? 'btn-primary'
                                                : 'btn-outline-primary'
                                        }`}
                                        onClick={() => {
                                            setSelectedSpecialtyId(sp.id);
                                            setSelectedDoctorId(null);
                                            setStep('doctor');
                                        }}
                                    >
                                        {sp.name}
                                    </button>
                                </div>
                            ))}
                        </div>
                        {specialties.length === 0 && (
                            <EmptyState
                                icon="fa-stethoscope"
                                title="No hay especialidades disponibles"
                                description="Por el momento no hay especialidades habilitadas para agendar citas. Intenta más tarde."
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Step: Doctor Selection */}
            {step === 'doctor' && (
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Selecciona un médico</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setStep('specialty')}
                            >
                                <i className="fas fa-arrow-left me-1"></i> Atrás
                            </button>
                        </div>
                        <div className="list-group">
                            {filteredDoctors.map((doc) => (
                                <button
                                    key={doc.id}
                                    className={`list-group-item list-group-item-action ${
                                        selectedDoctorId === doc.id ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        setSelectedDoctorId(doc.id);
                                        setStep('date');
                                    }}
                                >
                                    <strong>
                                        Dr. {doc.first_name} {doc.last_name}
                                    </strong>
                                    {doc.specialty && (
                                        <span className="ms-2 text-muted small">
                                            — {doc.specialty.name}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                        {filteredDoctors.length === 0 && (
                            <EmptyState
                                icon="fa-user-md"
                                title="No hay médicos en esta especialidad"
                                description="Por favor selecciona otra especialidad o intenta más tarde."
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Step: Date Selection */}
            {step === 'date' && (
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Selecciona una fecha</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setStep('doctor')}
                            >
                                <i className="fas fa-arrow-left me-1"></i> Atrás
                            </button>
                        </div>
                        {selectedDoctor && (
                            <p className="text-muted mb-3">
                                Médico: <strong>Dr. {selectedDoctor.first_name} {selectedDoctor.last_name}</strong>
                            </p>
                        )}
                        <input
                            type="date"
                            className="form-control"
                            min={today}
                            value={selectedDate}
                            onChange={(e) => {
                                setSelectedDate(e.target.value);
                                setSelectedSlot(null);
                            }}
                        />
                        {selectedDate && (
                            <button
                                className="btn btn-primary mt-3"
                                onClick={() => setStep('slot')}
                                disabled={!selectedDate}
                            >
                                Ver horarios disponibles
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Step: Slot Selection */}
            {step === 'slot' && (
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Selecciona un horario</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setStep('date')}
                            >
                                <i className="fas fa-arrow-left me-1"></i> Atrás
                            </button>
                        </div>
                        <p className="text-muted mb-3">
                            Fecha: <strong>{selectedDate}</strong>
                        </p>
                        {loadingSlots ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Cargando horarios...</span>
                                </div>
                            </div>
                        ) : slots.length > 0 ? (
                            <div className="row g-3">
                                {slots.map((slot) => (
                                    <div className="col-md-3" key={slot.id}>
                                        <button
                                            className={`btn w-100 ${
                                                selectedSlot?.id === slot.id
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={() => setSelectedSlot(slot)}
                                        >
                                            {slot.start_time.substring(0, 5)} —{' '}
                                            {slot.end_time.substring(0, 5)}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted">
                                No hay horarios disponibles para esta fecha. Intente otra fecha.
                            </p>
                        )}
                        {selectedSlot && (
                            <div className="mt-4">
                                <label className="form-label">Motivo (opcional)</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Ej: Control general, chequeo de rutina..."
                                />
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={handleBook}
                                    disabled={bookingLoading}
                                >
                                    {bookingLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Reservando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-check me-1"></i>
                                            Confirmar cita
                                        </>
                                    )}
                                </button>
                                {bookingError && (
                                    <div className="alert alert-danger mt-2">{bookingError}</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Step: Confirmation */}
            {step === 'confirm' && bookingResult && (
                <div className="card border-success">
                    <div className="card-body text-center">
                        <div className="mb-3">
                            <i className="fas fa-check-circle text-success" style={{ fontSize: 48 }}></i>
                        </div>
                        <h4>¡Cita agendada exitosamente!</h4>
                        <div className="mt-3">
                            <p className="mb-1">
                                <strong>Número de cita:</strong> {bookingResult.appointment_number}
                            </p>
                            <p className="mb-1">
                                <strong>Fecha:</strong> {bookingResult.scheduled_date}
                            </p>
                            <p className="mb-1">
                                <strong>Hora:</strong> {bookingResult.scheduled_time?.substring(0, 5)}
                            </p>
                            {bookingResult.reason && (
                                <p className="mb-1">
                                    <strong>Motivo:</strong> {bookingResult.reason}
                                </p>
                            )}
                        </div>
                        <div className="mt-4 d-flex justify-content-center gap-2">
                            <button className="btn btn-primary" onClick={resetForm}>
                                <i className="fas fa-plus me-1"></i> Agendar otra cita
                            </button>
                            <a href="/portal/appointments" className="btn btn-outline-primary">
                                <i className="fas fa-calendar-alt me-1"></i> Ver mis citas
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookAppointmentPage;
